import { DotsVerticalIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom";

import { StandardDialog } from "@/features/common/components/layout/StandardDialog";
import StandardPopoverButton from "@/features/common/components/layout/StandardPopoverButton";
import ReportPostDialogView from "@/features/post/components/ReportPostDialogView";
import { useEditPostButton } from "@/features/post/hooks/useEditPostButton";
import { useReportPostButton } from "@/features/post/hooks/useReportPostButton";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { PostDetailFragment } from "@/generated/generated-hooks";

type PostActionButtonProps = {
  postDetail: Pick<
    PostDetailFragment,
    | "_id"
    | "slug"
    | "writerId"
    | "title"
    | "hasCurrentUserReport"
    | "postStatus"
    | "createdAt"
  >;
  portalDomNodeSelector?: string;
};

export const PostActionButton = ({
  postDetail,
  portalDomNodeSelector,
}: PostActionButtonProps) => {
  const { user } = useUserContext();
  const [targetDomNode, setTargetDomNode] = useState<Element | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const { title, slug, _id, writerId } = postDetail;
  const isOwner = user?._id === writerId;

  const openReportPostDialog = () => setReportDialogOpen(true);

  const { onClickReportPostButton, reportButtonLabel, reportButtonDisabled } =
    useReportPostButton({ postDetail, openReportPostDialog });

  const { onClickEditPostButton, editPostDisabled, editPostLabel } =
    useEditPostButton({ postDetail });

  useEffect(() => {
    if (!portalDomNodeSelector) return;
    const buttonContainer = document.querySelector(portalDomNodeSelector);
    setTargetDomNode(buttonContainer);
  }, [portalDomNodeSelector]);

  const renderComponent = (color: string) => {
    if (isOwner) {
      const ownerOptions = [
        {
          content: editPostLabel,
          onClick: onClickEditPostButton,
          as: "button" as const,
          // disabled: editPostDisabled,
        },
      ];

      return (
        <StandardPopoverButton
          icon={<DotsVerticalIcon className={clsx("w-4 h-4 ml-1", color)} />}
          options={ownerOptions}
        />
      );
    }

    const userOptions = [
      {
        content: reportButtonLabel,
        onClick: onClickReportPostButton,
        as: "button" as const,
        disabled: reportButtonDisabled,
      },
    ];

    return (
      <>
        <StandardDialog open={reportDialogOpen}>
          <ReportPostDialogView
            post={{ _id, title, writerId, slug }}
            onClose={() => setReportDialogOpen(false)}
          />
        </StandardDialog>
        <StandardPopoverButton
          icon={<DotsVerticalIcon className={clsx("w-4 h-4 ml-1", color)} />}
          options={userOptions}
        />
      </>
    );
  };

  if (!targetDomNode || !portalDomNodeSelector) {
    return renderComponent("text-gray-500");
  }

  return ReactDOM.createPortal(renderComponent("text-gray-500"), targetDomNode);
};
